import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    Output,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';

import { forkJoin, Observable, Subject, Subscriber } from 'rxjs';
import { FocusingDirective } from "src/app/shared/ui/forms/inputs/focusing.directive";
import { LoadingNotifier } from '../../../../../shared/layout/loading-spinner';
import { OrderDetailServiceModel } from 'src/app/core/models/order-model';
import { OrderDetailServices } from '../../../../../core/services/order-services';
import {
    OrdersMapperService,
    OrderDetailChildLoadData,
    OrderDetailServiceView,
    OrderDetailIndividualView
} from '../../../shared';
import { OrderDetailIndividualListComponent } from '../order-detail-individual-list';
import { OrderDetailProductListComponent } from '../order-detail-product-list';
import { takeUntil } from 'rxjs/operators';
import { CountryReferenceModel } from 'src/app/core/models/reference-model/reference-general-model';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'op-order-detail-service-list',
    templateUrl: './orders-detail-service-list.component.html',
    providers: [OrdersMapperService]
})
export class OrdersDetailServiceListComponent implements OnDestroy {
    public readonly SPINNER_NAME: string = "orderDetailService";
    public readonly SPINNER_FULL_SCREEN: boolean = false;

    @Input("id") id: string;
    @Input("hideNotExist") hideWhenNotExist: boolean = false;
    @Input("collapeWhenLoad") collapeWhenLoad: boolean = false;
    @Input("showPanelCounter") showPanelCounter: boolean = false;
    @Input("countryReferences") countryReferences: CountryReferenceModel[] = [];
    @Input("orderDetailGuardianViews") orderDetailGuardianViews: OrderDetailIndividualView[] = new Array<OrderDetailIndividualView>();
    @Input() headerOrderId: string;
    @Input() showIndividualList: boolean = true;
    @Input() showProductList: boolean = true;

    @ViewChild(FocusingDirective) focusingDirective: FocusingDirective;
    @ViewChild("panel") panel: any;
    @ViewChildren(OrderDetailIndividualListComponent) individuals: QueryList<OrderDetailIndividualListComponent>;
    @ViewChildren(OrderDetailProductListComponent) ordersDetailProductComponents: QueryList<OrderDetailProductListComponent>;

    @Output("childLoadCompleted") childLoadCompleted = new EventEmitter<OrderDetailChildLoadData>();

    public collapsed: boolean;
    public focused: boolean;
    public loadingNotifier = new LoadingNotifier();
    public orderDetailServiceViews: OrderDetailServiceView[] = new Array<OrderDetailServiceView>();
    public orderLock: boolean;
    public countNumberOfItems: number = 0;
    private destroy$: Subject<boolean> = new Subject<boolean>();

    constructor(private changeDetection: ChangeDetectorRef,
        private orderDetailService: OrderDetailServices,
        private mapperService: OrdersMapperService) {

    }

    ngOnDestroy(): void {
        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    private getIndividualComponent(id: string): OrderDetailIndividualListComponent {
        return this.individuals.find(x => x.id === id);
    }
    private getProductComponent(id: string): OrderDetailProductListComponent {
        return this.ordersDetailProductComponents.find(x => x.id === id);
    }

    public loadOrderDetailServicesByOrder(parentOrderId: string, orderId: string, orderLock: boolean): Observable<boolean> {

        this.orderLock = orderLock;
        return new Observable(observer => {
            if (!orderId) {
                observer.next(true);
                observer.complete();
                return;
            }
            this.orderDetailService.getOrderDetailServicesByOrder(parentOrderId, orderId)
                .subscribe(
                    (result: OrderDetailServiceModel[]) => this.executeServiceResult(result, observer)
                )
        });
    }

    public loadOrderDetailServicesByOrderPartnerId(orderPartnerId: string, orderLock: boolean): Observable<boolean> {
        this.orderLock = orderLock;
        return new Observable(observer => {
            if (!orderPartnerId) {
                observer.next(true);
                observer.complete();
                return;
            }
            this.orderDetailService.getOrderDetailServiceByOrderPartnerId(orderPartnerId)
                .subscribe(
                    (result: OrderDetailServiceModel[]) => this.executeServiceResult(result, observer)
                )
        })
    }

    public loadOrderDetailServicesByOrderProductId(orderProductId: string, orderLock: boolean): Observable<boolean> {
        this.orderLock = orderLock;
        return new Observable(observer => {
            if (!orderProductId) {
                observer.next(true);
                observer.complete();
                return;
            }
            this.orderDetailService.getOrderDetailServiceByOrderProductId(orderProductId)
                .subscribe(
                    (result: OrderDetailServiceModel[]) => this.executeServiceResult(result, observer)
                )
        })
    }
    private executeServiceResult(result: OrderDetailServiceModel[], observer: Subscriber<boolean>) {
        this.fillNumberOfItems(result);
        this.orderDetailServiceViews = this.mapperService.toOrderDetailServiceViews(result);
        this.childLoadCompleted.emit({
            recordCount: result?.length
        });
        this.checkCollape();
        this.changeDetection.detectChanges();
        observer.next(true);
        observer.complete();
    }
    private checkCollape() {
        if (!this.orderDetailServiceViews || this.orderDetailServiceViews.length == 0 || this.collapeWhenLoad) {
            this.collapsed = true;
            return;
        }
        this.collapsed = false;
    }
    public getUnderlineClass(rowIndex: number): string {
        if (rowIndex === 0) {
            return "";
        }
        return "order-detail-start-section";
    }
    public togglePanel($event: MouseEvent) {
        this.panel.toggleCollapse($event);
    }
    public collapsedStatus(collapsed: any) {
        this.collapsed = collapsed;
    }
    public displayPartnerRow(view: OrderDetailServiceView) {
        if (!view || (!view.lastName && !view.firstName && !view.title)) {
            return false;
        }
        return true;
    }
    public hidePanel(): boolean {
        if (this.collapsed == true && this.hideWhenNotExist == true) {
            return true;
            }
        return false;
    }
    private fillNumberOfItems(models: OrderDetailServiceModel[]) {
        if (!models || models.length === 0) {
            this.countNumberOfItems = 0;
            return;
        }
        this.countNumberOfItems = models.length;
    }
    public showCounter(): boolean {
        return this.showPanelCounter && this.countNumberOfItems > 0;
    }

    public toggleDetail($event: MouseEvent, index: number, productDetailView: OrderDetailServiceView) {
        $event.preventDefault();
        if (this.orderDetailServiceViews[index].showDetail) {
            this.orderDetailServiceViews[index].showDetail = false;
            return;
        }

        this.orderDetailServiceViews[index].showDetail = true;
        this.changeDetection.detectChanges();
        this.loadingNotifier.show(this.SPINNER_NAME);
        this.fetchDrillDownPannel(productDetailView.serviceOrderId,
            this.headerOrderId,
            productDetailView.partnerOrderId,
            this.getIndividualComponent(productDetailView.orderServiceId),
            this.getProductComponent(productDetailView.orderServiceId),);
    }

    private fetchDrillDownPannel(serviceOrderId: string,
        parentOrderId: string,
        partnerOrderId: string,
        individualComponent: OrderDetailIndividualListComponent,
        productComponent: OrderDetailProductListComponent) {

        const dictionary = {};
        if (this.showIndividualList && individualComponent) {
            dictionary["individual"] = individualComponent.loadIndividualByPartnerId(parentOrderId, partnerOrderId);
        }

        if (this.showProductList && productComponent) {
            dictionary["product"] = productComponent.loadOrderProductSearchAtOrderItems(parentOrderId, serviceOrderId, this.orderLock);
        }

        forkJoin(dictionary)
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.loadingNotifier.hide(this.SPINNER_NAME)
            },
            () => this.loadingNotifier.hide(this.SPINNER_NAME));
    }

}
